import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { MotionConfig } from "framer-motion";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import Loader from "./components/Loader";
import CMS from "./pages/CMS";
import ProjectDetail from "./pages/ProjectDetail";

const Home = lazy(() => import("./pages/Home"));
const NotFound = lazy(() => import("./pages/NotFound"));

/** react-error-boundary v4+ requires an explicit fallback. */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center"
    >
      <p className="u-label text-muted-foreground">Something broke</p>
      <h1 className="text-h2 font-bold">This page hit an error.</h1>
      <p className="max-w-prose text-muted-foreground">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="u-label border border-border px-6 py-3 transition-colors hover:bg-primary hover:text-primary-foreground"
      >
        Try again
      </button>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <MotionConfig reducedMotion="user">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/cms" element={<CMS />} />
              <Route path="/project/:slug" element={<ProjectDetail />} />
              <Route
                path="*"
                element={
                  <Layout>
                    <Suspense fallback={<Loader />}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                }
              />
            </Routes>
            <Toaster />
          </BrowserRouter>
        </ErrorBoundary>
      </MotionConfig>
    </HelmetProvider>
  );
}
